import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, Link, useSearchParams } from 'react-router-dom';
import { adminAPI } from '../lib/api';
import { toast } from 'sonner';
import ConsentCompliancePanel from '../components/admin/ConsentCompliancePanel';
import {
  ChatCircleText, Users, UserPlus, Trophy, Crown, ArrowLeft, MagnifyingGlass,
  Check, X, Warning, PaperPlaneTilt, ClockCounterClockwise, ShieldSlash, Trash,
} from '@phosphor-icons/react';

const GROUP_DEFS = [
  { id: 'all_members', label: 'All Members', icon: Users, hint: 'Everyone with a valid, consented mobile number' },
  { id: 'needs_activation', label: 'Needs Activation', icon: UserPlus, hint: 'Admin-created members who never logged in' },
  { id: 'singles_ladder', label: 'Singles Ladder', icon: Trophy, hint: 'Current ladder participants' },
  { id: 'event_managers', label: 'Event Managers', icon: Crown, hint: 'Assigned managers across all events' },
];

const EVENT_KIND_LABELS = {
  round_robin: 'Round Robin',
  tournament: 'Tournament',
  club_event: 'Club Event',
  event_session: 'Event Session',
};

function estimateSegments(text) {
  if (!text) return 0;
  const n = text.length;
  const ucs2 = [...text].some((c) => c.charCodeAt(0) > 127);
  if (ucs2) return n <= 70 ? 1 : Math.ceil(n / 67);
  return n <= 160 ? 1 : Math.ceil(n / 153);
}

const FOOTER = 'Reply STOP to opt out.';

const SOURCE_META = {
  participants: { label: 'Event Participants', mode: 'participants', links: false },
  activation: { label: 'Needs Activation — Event Participants', mode: 'participants_unclaimed', links: true },
  managers: { label: 'Event Managers', mode: 'managers', links: false },
};

function backPathFor(kind, id) {
  switch (kind) {
    case 'tournament': return `/tournaments/${id}/manage`;
    case 'round_robin': return `/round-robins/${id}/manage`;
    case 'club_event': return `/admin/club-events/${id}`;
    case 'event_session': return `/admin/events/${id}`;
    default: return null;
  }
}

export default function AdminCommunicationsPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const eventCtx = useMemo(() => {
    const kind = searchParams.get('event_kind');
    const id = searchParams.get('event_id');
    if (!kind || !id) return null;
    const source = searchParams.get('source') || 'participants';
    const valid = !!EVENT_KIND_LABELS[kind] && !!SOURCE_META[source];
    return { event_kind: kind, event_id: id, source, event_name: searchParams.get('event_name') || '', valid };
  }, [searchParams]);
  const [view, setView] = useState(() => {
    const v = searchParams.get('view');
    return ['compose', 'history', 'suppressions', 'compliance'].includes(v) ? v : 'compose';
  });

  if (user?.role !== 'admin') return <Navigate to="/" replace />;

  return (
    <div className="space-y-6" data-testid="admin-comms-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#015e3f] text-white flex items-center justify-center">
            <ChatCircleText size={24} weight="duotone" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#0f2e22] leading-tight">Admin Communications</h1>
            <p className="text-sm text-[#5b6b63]">Direct SMS to members &middot; SMS only</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {[
            { id: 'compose', label: 'Compose', testid: 'comms-tab-compose' },
            { id: 'history', label: 'History', testid: 'comms-tab-history' },
            { id: 'suppressions', label: 'Opt-outs', testid: 'comms-tab-suppressions' },
            { id: 'compliance', label: 'Consent / Compliance', testid: 'comms-tab-compliance' },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={t.testid}
              onClick={() => setView(t.id)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                view === t.id ? 'bg-[#015e3f] text-white' : 'bg-white text-[#0f2e22] border border-[#e2e0da] hover:bg-[#f3f1ec]'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {view === 'compose' && <ComposeTab eventCtx={eventCtx} />}
      {view === 'history' && <HistoryTab />}
      {view === 'suppressions' && <SuppressionsTab />}
      {view === 'compliance' && <ConsentCompliancePanel />}
    </div>
  );
}

/* ============================ COMPOSE ============================ */

function ComposeTab({ eventCtx }) {
  const [groups, setGroups] = useState([]); // group ids
  const [eventsCatalog, setEventsCatalog] = useState({});
  const [selectedEvents, setSelectedEvents] = useState([]); // [{event_kind, event_id, label}]
  const [eventKind, setEventKind] = useState('round_robin');
  const [eventPick, setEventPick] = useState('');
  const [manualAdd, setManualAdd] = useState([]); // [{profile_id, name}]
  const [manualRemove, setManualRemove] = useState([]); // profile_ids
  const [message, setMessage] = useState('');
  const [includeLinks, setIncludeLinks] = useState(false);

  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState('');
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const [ctxBanner, setCtxBanner] = useState(null); // {sourceLabel, eventName, backPath}
  const [ctxError, setCtxError] = useState(null);
  const [catalogState, setCatalogState] = useState('loading'); // loading | ready | error
  const ctxApplied = useRef(false);

  useEffect(() => {
    adminAPI.commsListEvents()
      .then((r) => { setEventsCatalog(r.data || {}); setCatalogState('ready'); })
      .catch(() => setCatalogState('error'));
    adminAPI.listMembers().then((r) => setMembers(r.data?.members || [])).catch(() => {});
  }, []);

  // Reconstruct the event shortcut selection from the URL context once the
  // events catalog has loaded. Runs once; survives refresh (URL-driven).
  useEffect(() => {
    if (!eventCtx || ctxApplied.current || catalogState === 'loading') return;
    ctxApplied.current = true;
    if (!eventCtx.valid) {
      setCtxError('That event link is invalid. You can still compose a message normally.');
      return;
    }
    if (catalogState === 'error') {
      setCtxError('Could not load event details right now. You can still compose a message normally.');
      return;
    }
    const list = eventsCatalog[eventCtx.event_kind] || [];
    const found = list.find((e) => e.id === eventCtx.event_id);
    if (!found) {
      setCtxError('That event is no longer available. You can still compose a message normally.');
      return;
    }
    const meta = SOURCE_META[eventCtx.source] || SOURCE_META.participants;
    const eventName = found.label || eventCtx.event_name || 'this event';
    const label = `${meta.label} — ${eventName}`;
    setSelectedEvents([{ event_kind: eventCtx.event_kind, event_id: eventCtx.event_id, label, mode: meta.mode }]);
    if (meta.links) setIncludeLinks(true);
    setCtxBanner({ sourceLabel: meta.label, eventName, backPath: backPathFor(eventCtx.event_kind, eventCtx.event_id) });
    setPreview(null);
  }, [eventCtx, eventsCatalog, catalogState]);

  const toggleGroup = (id) => {
    setGroups((g) => (g.includes(id) ? g.filter((x) => x !== id) : [...g, id]));
    setPreview(null);
  };

  const addEvent = () => {
    if (!eventPick) return;
    const found = (eventsCatalog[eventKind] || []).find((e) => e.id === eventPick);
    if (!found) return;
    const label = `${EVENT_KIND_LABELS[eventKind]}: ${found.label}`;
    if (selectedEvents.some((e) => e.event_kind === eventKind && e.event_id === eventPick)) return;
    setSelectedEvents((s) => [...s, { event_kind: eventKind, event_id: eventPick, label }]);
    setEventPick('');
    setPreview(null);
  };

  const removeEvent = (ev) => {
    setSelectedEvents((s) => s.filter((e) => !(e.event_kind === ev.event_kind && e.event_id === ev.event_id)));
    if (ctxBanner && (!eventCtx || (ev.event_kind === eventCtx.event_kind && ev.event_id === eventCtx.event_id))) {
      setCtxBanner(null);
    }
    setPreview(null);
  };

  const spec = useMemo(() => ({
    groups,
    events: selectedEvents.map(({ event_kind, event_id, label, mode }) => ({ event_kind, event_id, label, mode })),
    manual_add: manualAdd.map((m) => m.profile_id),
    manual_remove: manualRemove,
    message,
    include_activation_links: includeLinks,
  }), [groups, selectedEvents, manualAdd, manualRemove, message, includeLinks]);

  const runPreview = useCallback(async () => {
    setLoadingPreview(true);
    setPreview(null);
    try {
      const r = await adminAPI.commsPreview(spec);
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    } finally {
      setLoadingPreview(false);
    }
  }, [spec]);

  const doSend = async () => {
    setSending(true);
    try {
      const r = await adminAPI.commsSend(spec);
      setResult(r.data);
      setConfirmOpen(false);
      toast.success(`Sent: ${r.data.accepted} accepted, ${r.data.failed} failed`);
      setPreview(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return members.filter((m) => {
      const name = (m.full_name || `${m.first_name || ''} ${m.last_name || ''}`).toLowerCase();
      const phone = (m.phone || '').toLowerCase();
      return name.includes(q) || phone.includes(q);
    }).slice(0, 25);
  }, [search, members]);

  const hasSelection = groups.length > 0 || selectedEvents.length > 0 || manualAdd.length > 0;
  const liveMsg = message + (includeLinks ? '\n[personal activation link]' : '') +
    (message.toLowerCase().includes('stop to opt out') ? '' : `\n\n${FOOTER}`);
  const segments = estimateSegments(liveMsg);

  const toggleManualAdd = (m) => {
    setManualAdd((cur) => cur.some((x) => x.profile_id === m.profile_id)
      ? cur.filter((x) => x.profile_id !== m.profile_id)
      : [...cur, { profile_id: m.profile_id, name: m.full_name || `${m.first_name || ''} ${m.last_name || ''}`.trim() }]);
    setPreview(null);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* LEFT: recipients */}
      <div className="space-y-5">
        {ctxBanner && (
          <div data-testid="comms-event-context" className="bg-[#eaf5ef] border border-[#bfe3cf] rounded-xl p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wide text-[#5b6b63] font-semibold">Source</p>
                <p className="text-sm font-semibold text-[#0f2e22]" data-testid="comms-context-source">{ctxBanner.sourceLabel}</p>
                <p className="text-[11px] uppercase tracking-wide text-[#5b6b63] font-semibold mt-2">Event</p>
                <p className="text-sm text-[#0f2e22] truncate" data-testid="comms-context-event">{ctxBanner.eventName}</p>
              </div>
              {ctxBanner.backPath && (
                <Link to={ctxBanner.backPath} data-testid="comms-back-to-event" className="text-sm text-[#015e3f] font-semibold flex items-center gap-1 shrink-0">
                  <ArrowLeft size={16} /> Back to Event
                </Link>
              )}
            </div>
          </div>
        )}
        {ctxError && (
          <div data-testid="comms-event-context-error" className="bg-[#fff6e9] border border-[#f3d9a8] rounded-xl p-4 text-sm text-[#a35b00]">
            {ctxError}
          </div>
        )}
        <section className="bg-white rounded-xl border border-[#e2e0da] p-5">
          <h2 className="text-base font-semibold text-[#0f2e22] mb-3">Recipient Groups</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {GROUP_DEFS.map((g) => {
              const active = groups.includes(g.id);
              const Icon = g.icon;
              return (
                <button
                  key={g.id}
                  data-testid={`comms-group-${g.id}`}
                  onClick={() => toggleGroup(g.id)}
                  className={`text-left p-3 rounded-lg border transition-colors ${
                    active ? 'border-[#015e3f] bg-[#eaf5ef]' : 'border-[#e2e0da] hover:bg-[#f7f6f3]'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Icon size={18} weight="duotone" className="text-[#015e3f]" />
                    <span className="text-sm font-semibold text-[#0f2e22]">{g.label}</span>
                    {active && <Check size={16} className="ml-auto text-[#015e3f]" />}
                  </div>
                  <p className="text-xs text-[#6b7a72] mt-1">{g.hint}</p>
                </button>
              );
            })}
          </div>
        </section>

        <section className="bg-white rounded-xl border border-[#e2e0da] p-5">
          <h2 className="text-base font-semibold text-[#0f2e22] mb-3">Event Participants</h2>
          <div className="flex gap-2 flex-wrap items-center">
            <select
              data-testid="comms-event-kind"
              value={eventKind}
              onChange={(e) => { setEventKind(e.target.value); setEventPick(''); }}
              className="px-3 py-2 rounded-lg border border-[#e2e0da] text-sm bg-white"
            >
              {Object.keys(EVENT_KIND_LABELS).map((k) => (
                <option key={k} value={k}>{EVENT_KIND_LABELS[k]}</option>
              ))}
            </select>
            <select
              data-testid="comms-event-pick"
              value={eventPick}
              onChange={(e) => setEventPick(e.target.value)}
              className="px-3 py-2 rounded-lg border border-[#e2e0da] text-sm bg-white flex-1 min-w-[160px]"
            >
              <option value="">Select an event…</option>
              {(eventsCatalog[eventKind] || []).map((e) => (
                <option key={e.id} value={e.id}>{e.label} {e.status ? `(${e.status})` : ''}</option>
              ))}
            </select>
            <button
              data-testid="comms-add-event"
              onClick={addEvent}
              disabled={!eventPick}
              className="px-3 py-2 rounded-lg bg-[#015e3f] text-white text-sm font-semibold disabled:opacity-40"
            >
              Add
            </button>
          </div>
          {selectedEvents.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {selectedEvents.map((ev) => (
                <span key={`${ev.event_kind}-${ev.event_id}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#eaf5ef] text-[#015e3f] text-xs font-medium">
                  {ev.label}
                  <button data-testid="comms-remove-event" onClick={() => removeEvent(ev)}><X size={14} /></button>
                </span>
              ))}
            </div>
          )}
        </section>

        <section className="bg-white rounded-xl border border-[#e2e0da] p-5">
          <h2 className="text-base font-semibold text-[#0f2e22] mb-3">Add / Remove Individuals</h2>
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9aa89f]" />
            <input
              data-testid="comms-member-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or phone…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-[#e2e0da] text-sm"
            />
          </div>
          {searchResults.length > 0 && (
            <div className="mt-2 max-h-56 overflow-auto border border-[#eceae4] rounded-lg divide-y divide-[#f1efe9]">
              {searchResults.map((m) => {
                const added = manualAdd.some((x) => x.profile_id === m.profile_id);
                return (
                  <button
                    key={m.profile_id}
                    data-testid={`comms-member-result-${m.profile_id}`}
                    onClick={() => toggleManualAdd(m)}
                    className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-[#f7f6f3]"
                  >
                    <span className="text-sm text-[#0f2e22]">{m.full_name || `${m.first_name || ''} ${m.last_name || ''}`}</span>
                    <span className="text-xs text-[#8a978e]">{m.phone || 'no phone'}</span>
                    {added
                      ? <Check size={16} className="text-[#015e3f] ml-2" />
                      : <UserPlus size={16} className="text-[#9aa89f] ml-2" />}
                  </button>
                );
              })}
            </div>
          )}
          {manualAdd.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-semibold text-[#6b7a72] mb-1">Manually added</p>
              <div className="flex flex-wrap gap-2">
                {manualAdd.map((m) => (
                  <span key={m.profile_id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#eef1fb] text-[#2a3c8f] text-xs font-medium">
                    {m.name}
                    <button onClick={() => toggleManualAdd(m)}><X size={14} /></button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </section>
      </div>

      {/* RIGHT: composer + preview */}
      <div className="space-y-5">
        <section className="bg-white rounded-xl border border-[#e2e0da] p-5">
          <h2 className="text-base font-semibold text-[#0f2e22] mb-3">Message</h2>
          <textarea
            data-testid="comms-message-input"
            value={message}
            onChange={(e) => { setMessage(e.target.value); setPreview(null); }}
            rows={6}
            placeholder="Write your SMS…  Tip: use {first_name} to personalize."
            className="w-full px-3 py-2 rounded-lg border border-[#e2e0da] text-sm resize-y"
          />
          <div className="flex items-center justify-between mt-2 text-xs text-[#6b7a72]">
            <span data-testid="comms-char-count">Characters: {message.length}</span>
            <span data-testid="comms-segment-count">Estimated SMS segments: {segments}</span>
          </div>
          {segments > 1 && (
            <div className="mt-2 flex items-center gap-2 text-xs text-[#a35b00] bg-[#fff6e9] border border-[#f3d9a8] rounded-lg px-3 py-2">
              <Warning size={14} weight="fill" />
              This message spans multiple SMS segments and may cost more per recipient.
            </div>
          )}
          <p className="text-[11px] text-[#8a978e] mt-2">"{FOOTER}" is added automatically to every message.</p>
          <label className="flex items-start gap-2 mt-3 cursor-pointer">
            <input
              data-testid="comms-include-links"
              type="checkbox"
              checked={includeLinks}
              onChange={(e) => { setIncludeLinks(e.target.checked); setPreview(null); }}
              className="mt-0.5"
            />
            <span className="text-sm text-[#0f2e22]">
              Include a personal <strong>account activation link</strong> for eligible unclaimed members
              <span className="block text-xs text-[#6b7a72]">Each unclaimed member gets their own unique link. Active members never receive one.</span>
            </span>
          </label>
        </section>

        <section className="bg-white rounded-xl border border-[#e2e0da] p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-[#0f2e22]">Recipients Preview</h2>
            <button
              data-testid="comms-preview-btn"
              onClick={runPreview}
              disabled={!hasSelection || loadingPreview}
              className="px-3 py-1.5 rounded-lg bg-[#0f2e22] text-white text-sm font-semibold disabled:opacity-40"
            >
              {loadingPreview ? 'Loading…' : 'Preview Recipients'}
            </button>
          </div>

          {!preview && <p className="text-sm text-[#8a978e]">Choose groups/events or add individuals, then preview.</p>}

          {preview && (
            <div className="space-y-3" data-testid="comms-preview-result">
              <div className="grid grid-cols-3 gap-2 text-center">
                <Stat label="Selected" value={preview.selected_count} testid="comms-stat-selected" />
                <Stat label="Eligible" value={preview.eligible_count} tone="good" testid="comms-stat-eligible" />
                <Stat label="Skipped" value={preview.skipped_count} tone="warn" testid="comms-stat-skipped" />
              </div>
              <div className="flex items-center justify-between text-xs text-[#6b7a72]">
                <span>Activation links: <strong data-testid="comms-stat-links">{preview.activation_link_count}</strong></span>
                <span>Est. segments / msg: <strong>{preview.segments_estimate}</strong></span>
              </div>
              {preview.selected_count === 0 && (
                <p className="text-sm text-[#a35b00] bg-[#fff6e9] border border-[#f3d9a8] rounded-lg px-3 py-2" data-testid="comms-no-recipients">
                  No recipients match this selection. Choose another group, event, or add individuals.
                </p>
              )}
              <RecipientList recipients={preview.recipients} />
              <button
                data-testid="comms-open-confirm"
                onClick={() => setConfirmOpen(true)}
                disabled={preview.eligible_count === 0}
                className="w-full py-2.5 rounded-lg bg-[#015e3f] text-white font-semibold disabled:opacity-40 flex items-center justify-center gap-2"
              >
                <PaperPlaneTilt size={18} weight="fill" />
                Send {preview.eligible_count} SMS Message{preview.eligible_count === 1 ? '' : 's'}
              </button>
            </div>
          )}
        </section>

        {result && (
          <section className="bg-[#eaf5ef] rounded-xl border border-[#bfe3cf] p-5" data-testid="comms-send-result">
            <h3 className="font-semibold text-[#015e3f] mb-2">Send complete</h3>
            <div className="text-sm text-[#0f2e22] grid grid-cols-2 gap-1">
              <span>Attempted: {result.attempted}</span>
              <span>Accepted: {result.accepted}</span>
              <span>Failed: {result.failed}</span>
              <span>Skipped: {result.skipped}</span>
              <span>Activation links: {result.activation_links}</span>
              <span>Mode: {result.mode}</span>
            </div>
          </section>
        )}
      </div>

      {confirmOpen && preview && (
        <ConfirmSend
          preview={preview}
          message={message}
          includeLinks={includeLinks}
          sending={sending}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={doSend}
        />
      )}
    </div>
  );
}

function Stat({ label, value, tone, testid }) {
  const color = tone === 'good' ? 'text-[#015e3f]' : tone === 'warn' ? 'text-[#a35b00]' : 'text-[#0f2e22]';
  return (
    <div className="bg-[#f7f6f3] rounded-lg py-2">
      <div className={`text-xl font-bold ${color}`} data-testid={testid}>{value}</div>
      <div className="text-[11px] text-[#6b7a72] uppercase tracking-wide">{label}</div>
    </div>
  );
}

function RecipientList({ recipients }) {
  const [showSkipped, setShowSkipped] = useState(true);
  return (
    <div>
      <label className="flex items-center gap-2 text-xs text-[#6b7a72] mb-1 cursor-pointer">
        <input type="checkbox" checked={showSkipped} onChange={(e) => setShowSkipped(e.target.checked)} data-testid="comms-toggle-skipped" />
        Show skipped recipients and reasons
      </label>
      <div className="max-h-64 overflow-auto border border-[#eceae4] rounded-lg divide-y divide-[#f1efe9]">
        {recipients.filter((r) => showSkipped || r.eligible).map((r) => (
          <div key={r.profile_id} className="flex items-center justify-between px-3 py-1.5 text-sm" data-testid={`comms-recipient-${r.profile_id}`}>
            <span className="text-[#0f2e22] truncate">{r.name}</span>
            <span className="flex items-center gap-2">
              {r.will_get_activation_link && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#eef1fb] text-[#2a3c8f] font-medium">link</span>}
              {r.eligible
                ? <span className="text-[11px] text-[#015e3f] font-medium">Eligible</span>
                : <span className="text-[11px] text-[#a35b00]">Skipped — {r.skip_label}</span>}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ConfirmSend({ preview, message, includeLinks, sending, onCancel, onConfirm }) {
  const finalMsg = message + (includeLinks && preview.activation_link_count ? '\n[personal activation link]' : '') +
    (message.toLowerCase().includes('stop to opt out') ? '' : `\n\n${FOOTER}`);
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/40" data-testid="comms-confirm-dialog">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl">
        <h3 className="text-lg font-bold text-[#0f2e22] mb-3">Send SMS</h3>
        <div className="grid grid-cols-3 gap-2 text-center mb-3">
          <Stat label="Selected" value={preview.selected_count} />
          <Stat label="Eligible" value={preview.eligible_count} tone="good" />
          <Stat label="Skipped" value={preview.skipped_count} tone="warn" />
        </div>
        {preview.groups?.length > 0 && (
          <p className="text-xs text-[#6b7a72] mb-2"><strong>Groups:</strong> {preview.groups.join(', ')}</p>
        )}
        <p className="text-xs text-[#6b7a72] mb-2"><strong>Activation links:</strong> {preview.activation_link_count}</p>
        <div className="bg-[#f7f6f3] rounded-lg p-3 text-sm text-[#0f2e22] whitespace-pre-wrap mb-3 max-h-40 overflow-auto">{finalMsg}</div>
        <p className="text-xs text-[#6b7a72] mb-4">Estimated {preview.eligible_count} recipients &middot; ~{preview.segments_estimate} segment(s) each.</p>
        <div className="flex gap-3">
          <button data-testid="comms-confirm-cancel" onClick={onCancel} disabled={sending} className="flex-1 py-2.5 rounded-lg border border-[#e2e0da] font-semibold text-[#0f2e22]">Cancel</button>
          <button data-testid="comms-confirm-send" onClick={onConfirm} disabled={sending} className="flex-1 py-2.5 rounded-lg bg-[#015e3f] text-white font-semibold disabled:opacity-50">
            {sending ? 'Sending…' : `Send ${preview.eligible_count} Messages`}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================ HISTORY ============================ */

function HistoryTab() {
  const [campaigns, setCampaigns] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminAPI.commsHistory().then((r) => setCampaigns(r.data?.campaigns || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const openDetail = async (id) => {
    try {
      const r = await adminAPI.commsHistoryDetail(id);
      setDetail(r.data);
    } catch (e) {
      toast.error('Could not load campaign');
    }
  };

  if (detail) {
    const c = detail.campaign;
    return (
      <div className="bg-white rounded-xl border border-[#e2e0da] p-5" data-testid="comms-history-detail">
        <button onClick={() => setDetail(null)} className="flex items-center gap-1 text-sm text-[#015e3f] font-semibold mb-3">
          <ArrowLeft size={16} /> Back to history
        </button>
        <div className="bg-[#f7f6f3] rounded-lg p-3 text-sm text-[#0f2e22] whitespace-pre-wrap mb-3">{c.message_body}</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center mb-4">
          <Stat label="Attempted" value={c.attempted_count} />
          <Stat label="Accepted" value={c.accepted_count} tone="good" />
          <Stat label="Failed" value={c.failed_count} tone="warn" />
          <Stat label="Links" value={c.activation_link_count} />
        </div>
        <div className="max-h-80 overflow-auto border border-[#eceae4] rounded-lg divide-y divide-[#f1efe9]">
          {detail.recipients.map((r) => (
            <div key={r.id} className="flex items-center justify-between px-3 py-1.5 text-sm">
              <span className="text-[#0f2e22] truncate">{r.name}</span>
              <span className="text-[11px]">
                {r.status === 'skipped'
                  ? <span className="text-[#a35b00]">Skipped — {r.skip_label || r.skip_reason}</span>
                  : r.status === 'failed'
                    ? <span className="text-[#b42318]">Failed {r.error_code ? `(${r.error_code})` : ''}</span>
                    : <span className="text-[#015e3f] capitalize">{r.status}</span>}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-[#e2e0da] p-5" data-testid="comms-history-list">
      <div className="flex items-center gap-2 mb-3">
        <ClockCounterClockwise size={18} className="text-[#015e3f]" />
        <h2 className="text-base font-semibold text-[#0f2e22]">Bulk SMS History</h2>
      </div>
      {loading && <p className="text-sm text-[#8a978e]">Loading…</p>}
      {!loading && campaigns.length === 0 && <p className="text-sm text-[#8a978e]">No campaigns sent yet.</p>}
      <div className="divide-y divide-[#f1efe9]">
        {campaigns.map((c) => (
          <button key={c.id} data-testid={`comms-campaign-${c.id}`} onClick={() => openDetail(c.id)} className="w-full text-left py-3 hover:bg-[#f9f8f5] px-1 rounded">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm text-[#0f2e22] truncate">{c.message_body}</p>
                <p className="text-xs text-[#8a978e]">{new Date(c.created_at).toLocaleString()} &middot; {c.sender_name}</p>
              </div>
              <div className="text-xs text-right text-[#6b7a72] shrink-0">
                <span className="text-[#015e3f] font-semibold">{c.accepted_count}</span> ok / <span className="text-[#a35b00]">{c.failed_count}</span> fail
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ============================ SUPPRESSIONS ============================ */

function SuppressionsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    adminAPI.commsListSuppressions().then((r) => setRows(r.data?.suppressions || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!phone.trim()) return;
    try {
      await adminAPI.commsAddSuppression(phone.trim());
      toast.success('Suppression added');
      setPhone('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add');
    }
  };

  const remove = async (p) => {
    try {
      await adminAPI.commsRemoveSuppression(p);
      toast.success('Removed');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove');
    }
  };

  const sourceLabel = { stop_reply: 'STOP reply', twilio: 'Carrier opt-out', manual: 'Manual' };

  return (
    <div className="bg-white rounded-xl border border-[#e2e0da] p-5" data-testid="comms-suppressions">
      <div className="flex items-center gap-2 mb-3">
        <ShieldSlash size={18} className="text-[#b42318]" />
        <h2 className="text-base font-semibold text-[#0f2e22]">SMS Opt-outs</h2>
      </div>
      <div className="flex gap-2 mb-4">
        <input
          data-testid="comms-suppress-phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Add a number to suppress (manual)…"
          className="flex-1 px-3 py-2 rounded-lg border border-[#e2e0da] text-sm"
        />
        <button data-testid="comms-suppress-add" onClick={add} className="px-4 py-2 rounded-lg bg-[#0f2e22] text-white text-sm font-semibold">Add</button>
      </div>
      <p className="text-xs text-[#8a978e] mb-3">STOP replies and carrier opt-outs are protected and can only clear when the member texts START/UNSTOP. Only manual entries can be removed here.</p>
      {loading && <p className="text-sm text-[#8a978e]">Loading…</p>}
      {!loading && rows.length === 0 && <p className="text-sm text-[#8a978e]">No opt-outs recorded.</p>}
      <div className="divide-y divide-[#f1efe9]">
        {rows.map((r) => (
          <div key={r.phone} className="flex items-center justify-between py-2.5" data-testid={`comms-suppression-${r.phone}`}>
            <div>
              <p className="text-sm text-[#0f2e22] font-medium">{r.phone}</p>
              <p className="text-xs text-[#8a978e]">{r.name ? `${r.name} · ` : ''}{sourceLabel[r.source] || r.source}{r.keyword ? ` · ${r.keyword}` : ''}</p>
            </div>
            {r.removable
              ? <button data-testid={`comms-suppress-remove-${r.phone}`} onClick={() => remove(r.phone)} className="flex items-center gap-1 text-xs text-[#b42318] font-semibold"><Trash size={14} /> Remove</button>
              : <span className="text-[11px] text-[#8a978e] italic">Protected</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
